(function (global) {
  'use strict';

  var DISCOUNTS = [0, 15, 30, 40];
  var VIP_BONUSES = [5, 10, 15, 20];

  function scaledForPrecision(value, factor) {
    var scaled = value * factor;
    var tolerance = Number.EPSILON * Math.max(1, Math.abs(scaled)) * 4;
    return scaled + tolerance;
  }

  function round(value, places) {
    var factor = Math.pow(10, places || 0);
    var sign = value < 0 ? -1 : 1;
    return sign * Math.round(scaledForPrecision(Math.abs(value), factor)) / factor;
  }

  function floor(value, places) {
    var factor = Math.pow(10, places || 0);
    return Math.floor(scaledForPrecision(value, factor)) / factor;
  }

  function clamp(value, low, high) {
    return Math.max(low, Math.min(high, value));
  }

  function nextFive(value) {
    return Math.ceil(value / 5) * 5;
  }

  function buildVip(price) {
    var multipliers = [0.5, 1, 2, 4];
    var minimumStep = Math.max(25, nextFive(price * 0.5));
    var previous = 0;

    return multipliers.map(function (multiplier, index) {
      var spend = nextFive(price * multiplier);
      if (index === 0) spend = Math.max(25, spend);
      if (spend <= previous) spend = nextFive(previous + minimumStep);
      previous = spend;
      var bonusRate = VIP_BONUSES[index];
      var bonus = round(spend * bonusRate / 100, 2);
      return {
        tier: index + 1,
        spend: spend,
        bonusRate: bonusRate,
        bonus: bonus,
        credit: round(spend + bonus, 2)
      };
    });
  }

  function buildMarginState(price, coldCost, repeatCost) {
    var newCeiling = round((1 - coldCost / price) * 100, 10);
    var repeatCeiling = round((1 - repeatCost / price) * 100, 10);
    var newCustomerState = 'buffered';
    if (newCeiling < 0) newCustomerState = 'loss';
    else if (newCeiling === 0) newCustomerState = 'breakeven';
    else if (newCeiling < 3) newCustomerState = 'unbuffered';

    return {
      newCeiling: newCeiling,
      newCeilingDisplay: floor(Math.max(0, newCeiling), 1),
      practicalCeiling: floor(Math.max(0, newCeiling - 3), 1),
      repeatCeiling: floor(Math.max(0, repeatCeiling), 1),
      newCustomerState: newCustomerState,
      repeatCustomerState: repeatCeiling < 0 ? 'loss' : 'breakeven'
    };
  }

  function calculate(input) {
    var price = Number(input.price);
    var landed = Number(input.landed);
    var ad = Number(input.ad);
    var courier = Number(input.courier);
    var hasRepeatRate = input.repeatRate !== null && input.repeatRate !== '' &&
      typeof input.repeatRate !== 'undefined' && isFinite(Number(input.repeatRate));
    var repeatRate = hasRepeatRate ? round(clamp(Number(input.repeatRate), 0, 100), 1) : 25;
    var coldCost = landed + courier + ad;
    var repeatCost = landed + courier;
    var margin = buildMarginState(price, coldCost, repeatCost);

    var profits = DISCOUNTS.map(function (discount) {
      var revenue = round(price * (1 - discount / 100), 2);
      return {
        discount: discount,
        revenue: revenue,
        newCustomer: round(revenue - coldCost, 2),
        repeatCustomer: round(revenue - repeatCost, 2)
      };
    });

    var atThirty = profits[2];
    var repeatOrders = repeatRate;
    var newOrders = 100 - repeatOrders;
    var newProfit = round(newOrders * atThirty.newCustomer, 2);
    var repeatProfit = round(repeatOrders * atThirty.repeatCustomer, 2);

    return {
      price: price,
      repeatRate: repeatRate,
      repeatRateIllustrative: !hasRepeatRate,
      newCeiling: margin.newCeiling,
      newCeilingDisplay: margin.newCeilingDisplay,
      practicalCeiling: margin.practicalCeiling,
      repeatCeiling: margin.repeatCeiling,
      newCustomerState: margin.newCustomerState,
      repeatCustomerState: margin.repeatCustomerState,
      profits: profits,
      per100: {
        newOrders: round(newOrders, 1),
        repeatOrders: round(repeatOrders, 1),
        newProfit: newProfit,
        repeatProfit: repeatProfit,
        total: round(newProfit + repeatProfit, 2),
        average: round((newProfit + repeatProfit) / 100, 2)
      },
      vip: buildVip(price)
    };
  }

  global.BDBlackFridayCalculatorMath = {
    calculate: calculate,
    buildVip: buildVip
  };

  if (!global.document) return;

  function money(value) {
    var absolute = Math.abs(value);
    var formatted = absolute.toLocaleString('en-NZ', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return (value < 0 ? '-$' : '$') + formatted;
  }

  function setText(root, key, value) {
    var node = root.querySelector('[data-bf-output="' + key + '"]');
    if (node) node.textContent = value;
  }

  function track(eventName, detail) {
    global.dataLayer = global.dataLayer || [];
    var payload = detail || {};
    payload.event = eventName;
    global.dataLayer.push(payload);
  }

  function init(root) {
    if (root.__bdBlackFridayCalculatorInitialized) return;
    root.__bdBlackFridayCalculatorInitialized = true;

    var inputNodes = Array.prototype.slice.call(root.querySelectorAll('[data-bf-input]'));
    var started = false;
    var completed = false;
    var viewed = false;
    var storageKey = 'bd-bf-calculator-inputs';
    var captureStorageKey = 'bd-bf-email-capture-tracked';

    function read() {
      var values = {};
      inputNodes.forEach(function (node) {
        var key = node.getAttribute('data-bf-input');
        values[key] = node.value.trim() === '' ? null : Number(node.value);
      });
      return values;
    }

    function isValid(values) {
      var hasRequiredCosts = values.landed !== null && values.ad !== null && values.courier !== null;
      return values.price !== null && values.price > 0 && hasRequiredCosts &&
        values.landed >= 0 && values.ad >= 0 && values.courier >= 0 &&
        (values.repeatRate === null || (values.repeatRate >= 0 && values.repeatRate <= 100));
    }

    function persist(values) {
      try { global.sessionStorage.setItem(storageKey, JSON.stringify(values)); } catch (_error) {}
    }

    function restore() {
      var saved;
      try { saved = JSON.parse(global.sessionStorage.getItem(storageKey) || 'null'); } catch (_error) {}
      if (!saved) return;
      inputNodes.forEach(function (node) {
        var key = node.getAttribute('data-bf-input');
        if (saved[key] === null) node.value = '';
        else if (typeof saved[key] !== 'undefined') node.value = saved[key];
      });
    }

    function render(trackResult) {
      var values = read();
      var error = root.querySelector('[data-bf-error]');
      if (!isValid(values)) {
        root.classList.add('has-error');
        if (error) error.textContent = 'Add a positive product price, make sure every cost is zero or more, and keep repeat-order share between 0% and 100%.';
        return;
      }

      root.classList.remove('has-error');
      if (error) error.textContent = '';
      persist(values);
      var result = calculate(values);

      setText(root, 'new-ceiling', result.newCustomerState === 'loss'
        ? 'Unprofitable'
        : result.newCeilingDisplay + '%');
      setText(root, 'practical-ceiling', result.newCustomerState === 'loss'
        ? 'Unavailable'
        : result.practicalCeiling + '%');
      setText(root, 'repeat-ceiling', result.repeatCustomerState === 'loss'
        ? 'Unprofitable'
        : result.repeatCeiling + '%');
      var ceilingMessage;
      if (result.newCustomerState === 'buffered') {
        ceilingMessage = 'Keep cold-customer discounts at or below ' + result.practicalCeiling + '% if you want a three-point profit buffer.';
      } else if (result.newCustomerState === 'unbuffered') {
        ceilingMessage = 'You are profitable at full price, but your margin is below a three-point buffer. There is no buffered discount available.';
      } else if (result.newCustomerState === 'breakeven') {
        ceilingMessage = 'You break even at full price on a cold-customer order. There is no discount or profit buffer available.';
      } else {
        ceilingMessage = 'You can’t discount to a cold customer at these numbers. Your product, fulfilment and ad costs already exceed the full-price revenue.';
      }
      setText(root, 'ceiling-message', ceilingMessage);

      result.profits.forEach(function (row, index) {
        setText(root, 'profit-new-' + index, money(row.newCustomer));
        setText(root, 'profit-repeat-' + index, money(row.repeatCustomer));
        var card = root.querySelector('[data-bf-profit-card="' + index + '"]');
        if (card) card.classList.toggle('is-loss', row.newCustomer < 0);
      });

      setText(root, 'per100-total', money(result.per100.total));
      setText(root, 'per100-average', money(result.per100.average) + ' average profit per order');
      setText(root, 'per100-mix', result.per100.newOrders + ' new orders contribute ' + money(result.per100.newProfit) +
        '. ' + result.per100.repeatOrders + ' repeat orders contribute ' + money(result.per100.repeatProfit) + '.');
      setText(root, 'repeat-note', result.repeatRateIllustrative
        ? 'Using an illustrative 25% repeat-order share. Add your real order share when you have it.'
        : 'Using your ' + result.repeatRate + '% repeat-order share.');

      result.vip.forEach(function (tier, index) {
        setText(root, 'vip-spend-' + index, money(tier.spend));
        setText(root, 'vip-bonus-' + index, tier.bonusRate + '% bonus');
        setText(root, 'vip-credit-' + index, money(tier.credit) + ' store credit');
      });

      if (trackResult && !completed) {
        completed = true;
        track('bd_black_friday_inputs_completed', {});
      }
      if (trackResult && !viewed) {
        viewed = true;
        track('bd_black_friday_result_viewed', {});
      }
    }

    restore();
    if (root.querySelector('[data-bf-unlocked]')) {
      root.classList.add('vip-is-unlocked');
    }
    if (root.querySelector('[data-bf-email-captured]')) {
      var captureTracked = false;
      try { captureTracked = global.sessionStorage.getItem(captureStorageKey) === 'true'; } catch (_error) {}
      if (!captureTracked) {
        track('bd_black_friday_email_captured', {});
        try { global.sessionStorage.setItem(captureStorageKey, 'true'); } catch (_error) {}
      }
    }

    inputNodes.forEach(function (node) {
      node.addEventListener('focus', function () {
        if (!started) {
          started = true;
          track('bd_black_friday_tool_started', {});
        }
      }, { once: true });
      node.addEventListener('input', function () { render(true); });
      node.addEventListener('change', function () { render(true); });
    });

    var callLink = root.querySelector('[data-bf-book-call]');
    if (callLink) callLink.addEventListener('click', function () {
      track('bd_black_friday_book_call_click', {});
    });

    render(false);
  }

  function boot() {
    Array.prototype.slice.call(global.document.querySelectorAll('[data-bf-calculator]')).forEach(init);
  }

  if (global.document.readyState === 'loading') global.document.addEventListener('DOMContentLoaded', boot);
  else boot();
})(window);
